/**
 * Knowledge Questions - Shows pending questions the avatar needs answered
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Chip,
    Collapse,
    IconButton,
    Alert,
    CircularProgress,
    Badge,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Send as SendIcon,
    Quiz as QuizIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../utils/api';

interface PendingQuestion {
    id: string;
    question: string;
    topic: string;
    session_id: string | null;
    is_answered: boolean;
    created_at: string;
}

interface KnowledgeQuestionsProps {
    userId: string;
}

const KnowledgeQuestions: React.FC<KnowledgeQuestionsProps> = ({ userId }) => {
    const { t } = useTranslation();
    const [questions, setQuestions] = useState<PendingQuestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(true);
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState<Record<string, boolean>>({});
    const [answered, setAnswered] = useState<Set<string>>(new Set());
    const [error, setError] = useState<string | null>(null);

    const fetchQuestions = useCallback(async () => {
        try {
            setLoading(true);
            const data = await apiClient.get<PendingQuestion[]>(
                `/api/v1/avatar/knowledge/pending/${userId}`
            );
            setQuestions(data);
            setError(null);
        } catch (err: any) {
            console.error('Failed to fetch pending questions:', err);
            setError(err.message || 'Failed to load questions');
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions]);

    const handleAnswerChange = (questionId: string, value: string) => {
        setAnswers(prev => ({ ...prev, [questionId]: value }));
    };

    const handleSubmitAnswer = async (questionId: string) => {
        const answer = answers[questionId]?.trim();
        if (!answer) return;

        setSubmitting(prev => ({ ...prev, [questionId]: true }));
        try {
            await apiClient.post(`/api/v1/avatar/knowledge/answer/${questionId}`, {
                answer,
            });
            setAnswered(prev => new Set(prev).add(questionId));
            setAnswers(prev => {
                const next = { ...prev };
                delete next[questionId];
                return next;
            });
        } catch (err: any) {
            console.error('Failed to submit answer:', err);
            setError(err.message || 'Failed to save answer');
        } finally {
            setSubmitting(prev => ({ ...prev, [questionId]: false }));
        }
    };

    const unansweredQuestions = questions.filter(q => !answered.has(q.id));

    if (loading) {
        return (
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                        Loading questions...
                    </Typography>
                </Box>
            </Paper>
        );
    }

    if (unansweredQuestions.length === 0 && answered.size === 0) {
        return null; // Don't show anything if no pending questions
    }

    return (
        <Paper sx={{ mb: 3, overflow: 'hidden' }}>
            {/* Header */}
            <Box
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'info.main',
                    color: 'info.contrastText',
                    cursor: 'pointer',
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <Badge badgeContent={unansweredQuestions.length} color="error">
                        <QuizIcon />
                    </Badge>
                    <Typography variant="h6">
                        Help Your Avatar Learn
                    </Typography>
                </Box>
                <IconButton size="small" sx={{ color: 'inherit' }}>
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            <Collapse in={expanded}>
                <Box sx={{ p: 2 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Your AI avatar encountered questions it couldn't answer about you.
                        Answering these will help it represent you better in future conversations.
                    </Typography>

                    {/* Answered feedback */}
                    {answered.size > 0 && (
                        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
                            {answered.size} question{answered.size > 1 ? 's' : ''} answered! Your avatar will remember this.
                        </Alert>
                    )}

                    {/* Questions list */}
                    {unansweredQuestions.map((question) => (
                        <Paper
                            key={question.id}
                            variant="outlined"
                            sx={{ p: 2, mb: 2 }}
                        >
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                {question.topic && (
                                    <Chip
                                        label={question.topic}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    {new Date(question.created_at).toLocaleDateString()}
                                </Typography>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                                {question.question}
                            </Typography>

                            <Box display="flex" gap={1}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Type your answer..."
                                    value={answers[question.id] || ''}
                                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSubmitAnswer(question.id);
                                        }
                                    }}
                                    disabled={submitting[question.id]}
                                />
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleSubmitAnswer(question.id)}
                                    disabled={!answers[question.id]?.trim() || submitting[question.id]}
                                    startIcon={submitting[question.id] ? <CircularProgress size={16} /> : <SendIcon />}
                                >
                                    Send
                                </Button>
                            </Box>
                        </Paper>
                    ))}

                    {unansweredQuestions.length === 0 && answered.size > 0 && (
                        <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                            All questions answered! Your avatar is learning.
                        </Typography>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
};

export default KnowledgeQuestions;
